import { SubmitHandler, useWatch } from "react-hook-form";
import { useStudioWizardForm } from "./useStudioWizardForm";
import {
  IStudioWizardApiModel,
  IStudioWizardFormModel,
} from "../models/studioWizardFormModel";
import { joinNotificationEmails } from "../models/reservationNotificationEmails";
import { useReservationNotificationEmails } from "./useReservationNotificationEmails";
import { PageMode } from "../../../../../common/types";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { useNavigate, useParams } from "react-router-dom";
import { routePaths } from "../../../../../common/infrastructure/routes/routePaths";
import { useAppContext } from "../../../../../AppContext";
import { usePermissions } from "../../../../../common/infrastructure/hooks/usePermissions";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { normalizePhone } from "../../../../../common/components/forms/inputs/phoneField/utils/PhoneInputUtility";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { IWorkingHoursData } from "../../../../../common/components/common/workingHoursDialog/models/models";
import {
  getCommissionsVisibility,
  getStudioType,
  getHiddenPaymentTypes,
  getTimeStep,
} from "../utils/studioWizardUtils";
import { getCurrentUserRole } from "../../../../../common/infrastructure/helpers/userHelper";
import {
  UserRole,
  PaymentTypes,
  StudioType,
  FeatureFlagKey,
} from "../../../../../common/enums";
import { useFeatureFlag } from "../../../../../common/infrastructure/hooks/useFeatureFlag";
import { useMemo } from "react";
import { generateTimezoneOptions } from "../../../../../common/infrastructure/utilities/timezonesUtility";
import { getPaymentOptions } from "../../../../../common/utilities/paymentUtility";

export const useStudioWizardPage = (pageMode: PageMode) => {
  const { resetStudios, isDesktopOrTablet, currentUser } = useAppContext();
  const { mutate: mutate } = useApiMutation();

  const navigate = useNavigate();
  const { id } = useParams();
  const { userHavePermissions } = usePermissions();
  const studioTypeOptions = getStudioType();
  const timeStepOptions = getTimeStep();
  const commissionsVisibilityOptions = getCommissionsVisibility();

  const canSelectOwner = userHavePermissions(
    endpointsMetadata.studio.getAvailableTenants,
  );

  const isGlobalAdmin = useMemo(
    () => getCurrentUserRole(currentUser) === UserRole.GlobalAdmin,
    [currentUser],
  );

  const { data: tenantOptions, isLoading } = useApiQuery<ISelectFieldModel[]>({
    queryKey: [endpointsMetadata.studio.getAvailableTenants],
    endpoint: endpointsMetadata.studio.getAvailableTenants,
    enabled: canSelectOwner,
    globalIsLoading: false,
  });

  const { data: studioResult } = useApiQuery<IStudioWizardApiModel>({
    queryKey: [endpointsMetadata.studio.wizardGetStudio, id],
    endpoint: `${endpointsMetadata.studio.wizardGetStudio}?studioId=${id}`,
    enabled: id !== undefined,
    globalIsLoading: true,
  });

  const { data: workingHoursData } = useApiQuery<IWorkingHoursData>({
    queryKey: [endpointsMetadata.workingHours.getStudioWorkingHours, id],
    endpoint: `${endpointsMetadata.workingHours.getStudioWorkingHours}/${id}`,
    enabled: pageMode !== "add",
  });

  const timezoneOptions = generateTimezoneOptions();
  const defaultBulgarianTimezone = timezoneOptions.find(
    (x) => x.name === "GMT+2",
  )?.id;

  const form = useStudioWizardForm(
    id,
    tenantOptions,
    studioResult,
    defaultBulgarianTimezone,
  );

  const reservationNotificationEmails = useReservationNotificationEmails(form);

  // The device-notification reminder only makes sense while app notifications are on, but a
  // saved reminder stays visible so it can still be turned off. The saved value (not the live
  // one) decides, so the field does not vanish while it is being cleared.
  const appNotificationsEnabled = useFeatureFlag(
    FeatureFlagKey.EnableAppNotifications,
    false,
  );
  const canUseDeviceNotifications =
    appNotificationsEnabled || studioResult?.pushReminderHoursBefore != null;

  const pageTitle = `${pageMode === "edit" ? "Редакция" : "Добавяне"} на обект`;

  const actionButtonLabel = `${
    pageMode === "edit" ? "Запазване на промените" : "Добавяне"
  }`;

  const supportedTypesOfPayment = form.watch("supportedTypesOfPayment");

  const isMultisportAvailable = useMemo(
    () => !!supportedTypesOfPayment?.find((x) => x === PaymentTypes.Multisport),
    [supportedTypesOfPayment],
  );

  const isCoolfitAvailable = useMemo(
    () => !!supportedTypesOfPayment?.find((x) => x === PaymentTypes.CoolFit),
    [supportedTypesOfPayment],
  );

  const onSave: SubmitHandler<IStudioWizardFormModel> = async (data) => {
    if (data.phoneNumber) {
      data.phoneNumber = normalizePhone(data.phoneNumber)!;
    }

    if (data.secondaryPhoneNumber) {
      data.secondaryPhoneNumber = normalizePhone(data.secondaryPhoneNumber)!;
    }

    // The form keeps one row per recipient; the API expects a single
    // comma-separated string. Blank rows the user left behind are dropped.
    const { reservationNotificationEmails, ...rest } = data;
    const payload = {
      ...rest,
      reservationNotificationEmail: joinNotificationEmails(
        reservationNotificationEmails,
      ),
    };

    mutate(
      {
        endpoint: endpointsMetadata.studio.wizard,
        globalIsLoading: true,
        method: "post",
        invalidateKeys: [
          [endpointsMetadata.studio.getAll],
          [endpointsMetadata.initialization.getUserStudios],
        ],
        ...payload,
      },
      {
        onSuccess: () => {
          // Same browser session, same admin: re-picking the studio is not a new opening.
          resetStudios({ keepStudioAccessConfirmation: true });
          navigate(routePaths.studios);
        },
      },
    );
  };

  const types = form.watch("types");
  const usersCanCancelReservations = form.watch("usersCanCancelReservations");
  const allowLateCancellationsWithPayment = form.watch(
    "allowLateCancellationsWithPayment",
  );
  const studioDoesNotUsePlans = form.watch("studioDoesNotUsePlans");

  return {
    id,
    form,
    pageTitle,
    logoUrl: studioResult?.logoUrl ?? null,
    tenantOptions: tenantOptions,
    isLoading,
    actionButtonLabel,
    canSelectOwner,
    isDesktopOrTablet,
    workingHoursData,
    studioTypeOptions,
    timeStepOptions,
    commissionsVisibilityOptions,
    isGlobalAdmin,
    timezoneNames: timezoneOptions,
    paymentOptions: getPaymentOptions(),
    hiddenPaymentTypes: getHiddenPaymentTypes(!!studioDoesNotUsePlans),
    isMultisportAvailable,
    isCoolfitAvailable,
    showGroupStudioTypeOptions: types?.includes(StudioType.Group) ?? false,
    showServiceStudioTypeOptions: types?.includes(StudioType.Service) ?? false,
    showGymStudioTypeOptions: types?.includes(StudioType.Gym) ?? false,
    showStoreStudioTypeOptions: types?.includes(StudioType.Store) ?? false,
    usersCanCancelReservations,
    allowLateCancellationsWithPayment,
    studioDoesNotUsePlans,
    reservationNotificationEmails,
    canUseDeviceNotifications,
    onSave,
  };
};
